import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import { getAskValidators, getMyAsk } from "../../../../_api/AskValidation";
import { SideBar } from "../../../../_helpers/components/SideBar";
import { InputForm } from "../../../../_helpers/components/Forms/InputLabel";
import { Button } from "../../../../_helpers/components/Button";
import toast from "react-hot-toast";
import { validateRequest, refuseRequest } from "../../../../_api/AskValidation";
import { getUserCounters } from "../../../../_api/Counter";
import { colorIsDark, floatRoundToString } from "../../../../_helpers/functions";

const requestMenu = [
    {
        title: "Toutes",
        to: "/validation#ALL",
        svg: "M9 12l2 2 4-4M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z",
        color: "gray",
    },
    {
        title: "En attente",
        to: "/validation#PENDING",
        svg: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
        color: "yellow",
    },
    {
        title: "Rejetées",
        to: "/validation#REFUSED",
        svg: "M6 18L18 6M6 6l12 12",
        color: "red",
    },
    {
        title: "Annulées",
        to: "/validation#CANCELED",
        svg: "M6 18L18 6M6 6l12 12",
        color: "gray",
    },
    {
        title: "Acceptées",
        to: "/validation#ACCEPTED",
        svg: "M5 13l4 4L19 7",
        color: "green",
    },
    {
        title: "Passées",
        to: "/validation#PAST",
        svg: "M5 13l4 4L19 7",
        color: "gray",
    },
];

const statusToText = {
    'ACCEPTED': 'Acceptée',
    'REFUSED': 'Refusée',
    'PENDING': 'En attente',
    'PAST': 'Passée',
    'CANCELED': 'Annulée',
}

const statusColors = {
    'ACCEPTED': {
        bg: 'bg-green-100',
        text: 'text-green-800',
        border: 'border-green-200',
        icon: 'M5 13l4 4L19 7'
    },
    'REFUSED': {
        bg: 'bg-red-100',
        text: 'text-red-800',
        border: 'border-red-200',
        icon: 'M6 18L18 6M6 6l12 12'
    },
    'PENDING': {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        border: 'border-yellow-200',
        icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
    },
    'PAST': {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        border: 'border-blue-200',
        icon: 'M5 13l4 4L19 7'
    },
    'CANCELED': {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        border: 'border-gray-200',
        icon: 'M6 18L18 6M6 6l12 12'
    },
}

const formatDateHours = (dateInput) => {
    const date = new Date(dateInput);
    const pad = (num) => (num < 10 ? "0" + num : num);
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatDateDDMMYYYY = (dateInput) => {
    const date = new Date(dateInput);
    const pad = (num) => (num < 10 ? "0" + num : num);
    return `${pad(date.getDate())}/${pad(
        date.getMonth() + 1
    )}/${date.getFullYear()}`;
};

const formatDateTime = (dateInput) => {
    const date = new Date(dateInput);
    const pad = (num) => (num < 10 ? "0" + num : num);
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} à ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const StatusBadge = ({ status }) => {
    const statusConfig = statusColors[status] || statusColors['PENDING'];

    return (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border} border`}>
            <svg
                className="w-4 h-4 mr-1.5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={statusConfig.icon}
                />
            </svg>
            {statusToText[status] || status}
        </span>
    );
};

const ValidationCard = ({ validation, onClick }) => {

    const statusConfig = statusColors[validation.status] || statusColors['PENDING'];
    const isPending = validation.status === 'PENDING';
    const isPlanningCorrection = validation.hour_type == null && validation.hour_type_id == null;
    const requestLabel = isPlanningCorrection
        ? "Demande de correction de planning"
        : (validation.hour_type?.name || "Type d'heure");

    return (
        <div
            onClick={() => onClick(validation)}
            className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden group"
        >
            <div className={`h-1 ${statusConfig.bg.replace('bg-', 'bg-').replace('-100', '-500')}`}></div>
            <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                            <div className="flex items-center justify-center w-10 h-10 bg-linear-to-br from-app-primary to-app-secondary rounded-full text-white font-semibold text-sm">
                                {validation.user?.first_name?.[0]?.toUpperCase() || ""}
                                {validation.user?.last_name?.[0]?.toUpperCase() || ""}
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-app-primary transition-colors">
                                    {validation.user?.first_name} {validation.user?.last_name}
                                </h3>
                                <p className="text-sm text-gray-500">
                                    {formatDateTime(validation.created_at)}
                                </p>
                            </div>
                        </div>
                    </div>
                    <StatusBadge status={validation.status} />
                </div>

                <div className="space-y-3">
                    <div className="flex items-start space-x-2">
                        <svg
                            className="w-5 h-5 text-gray-400 mt-0.5 shrink-0"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                        </svg>
                        <div className="flex-1">
                            <p className="text-sm font-medium text-gray-700">
                                {requestLabel}
                            </p>
                            <p className="text-sm text-gray-600">
                                {validation.start_date !== validation.end_date
                                    ? `Du ${formatDateDDMMYYYY(validation.start_date)} au ${formatDateDDMMYYYY(validation.end_date)}`
                                    : `Le ${formatDateDDMMYYYY(validation.start_date)}`}
                            </p>
                        </div>
                    </div>

                    {validation.remarks && (
                        <div className="flex items-start space-x-2">
                            <svg
                                className="w-5 h-5 text-gray-400 mt-0.5 shrink-0"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                                />
                            </svg>
                            <p className="text-sm text-gray-600 line-clamp-2">
                                {validation.remarks}
                            </p>
                        </div>
                    )}

                    {isPlanningCorrection && validation.timeslots && validation.timeslots.length > 0 && (
                        <div className="flex items-start space-x-2">
                            <svg
                                className="w-5 h-5 text-gray-400 mt-0.5 shrink-0"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-700 mb-1">
                                    {validation.timeslots.length} tranche{validation.timeslots.length > 1 ? 's' : ''} horaire{validation.timeslots.length > 1 ? 's' : ''}
                                </p>
                                <div className="grid gap-2">
                                    {validation.timeslots.slice(0, 3).map((ts) => (
                                        <span
                                            key={ts.id}
                                            className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700"
                                        >
                                            {ts.hour_type.name} de {formatDateHours(ts.start_date)} à {formatDateHours(ts.end_date)}
                                        </span>
                                    ))}
                                    {validation.timeslots.length > 3 && (
                                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                            +{validation.timeslots.length - 3} autre{validation.timeslots.length - 3 > 1 ? 's' : ''}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {validation.number_validation > 0 && (
                        <div className="flex items-center space-x-2">
                            <svg
                                className="w-5 h-5 text-gray-400"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            <p className="text-sm text-gray-600">
                                {validation.number_validation} autorisation{validation.number_validation > 1 ? 's' : ''} requise{validation.number_validation > 1 ? 's' : ''}
                            </p>
                        </div>
                    )}

                    {validation.response && (
                        <div className={`mt-3 p-3 rounded-lg ${statusConfig.bg} ${statusConfig.border} border`}>
                            <p className={`text-sm font-medium ${statusConfig.text}`}>
                                Réponse : {validation.response}
                            </p>
                        </div>
                    )}
                </div>

                {isPending && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-xs text-gray-500 flex items-center">
                            <svg
                                className="w-4 h-4 mr-1 animate-pulse"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            En attente de validation
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

const AskValidationForm = (props) => {
    const { validation, closeModal, refreshData, isValidation } = props;
    const [isProcessing, setIsProcessing] = useState(false);
    const [response, setResponse] = useState(validation.response || "");
    const [timeslotsOpen, setTimeslotsOpen] = useState(false);
    const [userCountersOpen, setUserCountersOpen] = useState(false);
    const [userCounters, setUserCounters] = useState([]);
    const [userCountersLoading, setUserCountersLoading] = useState(false);
    const [userCountersLoadedForUserId, setUserCountersLoadedForUserId] = useState(null);

    const formatCounterValue = (rawValue, rawType) => {
        const type = `${rawType ?? ""}`.toUpperCase();
        const value = Number.parseFloat(rawValue);
        if (!Number.isFinite(value)) return "-";

        if (type === "DAYS") {
            return `${floatRoundToString(value, 3)}j`;
        }

        if (type === "HOURS") {
            const sign = value < 0 ? "-" : "";
            const abs = Math.abs(value);
            const totalMinutes = Math.round(abs * 60);
            const h = Math.floor(totalMinutes / 60);
            const m = totalMinutes % 60;
            return m === 0 ? `${sign}${h}h` : `${sign}${h}h${String(m).padStart(2, "0")}`;
        }

        return floatRoundToString(value, 3);
    };

    useEffect(() => {
        if (!userCountersOpen) return;

        const userId = validation.user?.id;
        if (!userId) {
            setUserCounters([]);
            setUserCountersLoading(false);
            setUserCountersLoadedForUserId(null);
            return;
        }

        if (String(userCountersLoadedForUserId) === String(userId)) return;

        let cancelled = false;
        setUserCountersLoading(true);

        getUserCounters(userId)
            .then((resp) => {
                if (cancelled) return;
                setUserCounters(resp.data ?? []);
                setUserCountersLoadedForUserId(userId);
            })
            .catch(() => {
                if (cancelled) return;
                setUserCounters([]);
                setUserCountersLoadedForUserId(userId);
            })
            .finally(() => {
                if (cancelled) return;
                setUserCountersLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [userCountersOpen, validation.user?.id, userCountersLoadedForUserId]);

    const _validate = () => {
        setIsProcessing(true);
        const payload = {
            operation: "ACCEPTED",
            response: response,

        }
        validateRequest(validation.id, payload)
            .then((resp) => {
                toast.success("Demande validée avec succès.");
                refreshData();
                closeModal();
            })
            .catch((error) => {
                toast.error(error.response?.data?.message || "Une erreur est survenue.");
            })
            .finally(() => {
                setIsProcessing(false);
            });
    };

    const _cancel = () => {
        setIsProcessing(true);
        const payload = {
            operation: "CANCELED",
            response: "Demande annulée par l'utilisateur"

        }
        refuseRequest(validation.id, payload)
            .then((resp) => {
                toast.success("Demande annulée avec succès.");
                refreshData();
                closeModal();
            })
            .catch((error) => {
                toast.error(error.response?.data?.message || "Une erreur est survenue.");
            })
            .finally(() => {
                setIsProcessing(false);
            });
    };

    const _refuse = () => {
        setIsProcessing(true);
        const payload = {
            operation: "REFUSED",
            response: response,

        }
        refuseRequest(validation.id, payload)
            .then((resp) => {
                toast.success("Demande refusée.");
                refreshData();
                closeModal();
            })
            .catch((error) => {
                toast.error(error.response?.data?.message || "Une erreur est survenue.");
            })
            .finally(() => {
                setIsProcessing(false);
            });
    };

    const statusConfig = statusColors[validation.status] || statusColors['PENDING'];
    const isPending = validation.status === 'PENDING';
    const isPlanningCorrection = validation.hour_type == null && validation.hour_type_id == null;
    const requestLabel = isPlanningCorrection
        ? "Demande de correction de planning"
        : (validation.hour_type?.name || "Non spécifié");

    return (
        <div className="space-y-6">
            {/* Header avec statut */}
            <div className={`p-4 rounded-lg ${statusConfig.bg} ${statusConfig.border} border`}>
                <div className="flex items-center justify-between">
                    <div>
                        <p className={`text-sm font-medium ${statusConfig.text} mb-1`}>Statut</p>
                        <StatusBadge status={validation.status} />
                    </div>
                    {validation.number_validation > 0 && (
                        <div className="text-right">
                            <p className={`text-sm font-medium ${statusConfig.text} mb-1`}>Autorisations</p>
                            <p className={`text-2xl font-bold ${statusConfig.text}`}>
                                {validation.number_validation}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Informations utilisateur */}
            <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <svg
                        className="w-5 h-5 mr-2 text-gray-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                    </svg>
                    Demandeur
                </h4>
                <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-linear-to-br from-app-primary to-app-secondary rounded-full text-white font-bold text-lg">
                        {validation.user?.first_name?.[0]?.toUpperCase() || ""}
                        {validation.user?.last_name?.[0]?.toUpperCase() || ""}
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900">
                            {validation.user?.first_name} {validation.user?.last_name}
                        </p>
                        {validation.user?.email && (
                            <p className="text-sm text-gray-500">{validation.user.email}</p>
                        )}

                        <div className="mt-2">
                            <button
                                type="button"
                                onClick={() => setUserCountersOpen((v) => !v)}
                                className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-800 hover:underline focus:outline-none"
                                aria-expanded={userCountersOpen}
                            >
                                <span>Compteurs</span>
                                <svg
                                    className={`w-4 h-4 transition-transform ${userCountersOpen ? "rotate-180" : ""}`}
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {userCountersOpen && (
                                <div className="mt-2">
                                    {userCountersLoading ? (
                                        <div className="text-xs text-gray-500">Chargement…</div>
                                    ) : userCounters.length === 0 ? (
                                        <div className="text-xs text-gray-500">Aucun compteur.</div>
                                    ) : (
                                        <div className="flex flex-wrap gap-1.5">
                                            {userCounters.map((counter) => (
                                                <span
                                                    key={counter.id}
                                                    className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-0.5 text-xs"
                                                    title={counter.name}
                                                >
                                                    <span className="max-w-56 truncate font-medium text-gray-700">
                                                        {counter.name}
                                                    </span>
                                                    <span className="font-semibold tabular-nums text-gray-900">
                                                        {formatCounterValue(counter.total_mvt, counter.type)}
                                                    </span>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Informations de la demande */}
            <div className="space-y-4">
                <div>
                    {!isPlanningCorrection && (
                        <div className="p-3 rounded-lg border border-gray-200" style={{ backgroundColor: validation.hour_type?.bg_color + "80" }}>
                            <p className="font-medium" style={{ color: colorIsDark(validation?.hour_type?.bg_color) ? '#d0d0d0' : '#0d0d0d' }}>
                                {requestLabel}
                            </p>
                        </div>
                    )}
                    {isPlanningCorrection && (
                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-gray-900 font-medium">
                                {requestLabel}
                            </p>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date de demande
                        </label>
                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-gray-900 text-xs">
                                {formatDateTime(validation.created_at)}
                            </p>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Période
                        </label>
                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-gray-900 text-xs">
                                {validation.start_date !== validation.end_date
                                    ? `${formatDateDDMMYYYY(validation.start_date)} - ${formatDateDDMMYYYY(validation.end_date)}`
                                    : formatDateDDMMYYYY(validation.start_date)}
                            </p>
                        </div>
                    </div>
                </div>

                {validation.timeslots && validation.timeslots.length > 0 && (
                    <div>
                        <button
                            type="button"
                            onClick={() => setTimeslotsOpen((v) => !v)}
                            className="w-full flex items-center justify-between text-left p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-app-primary"
                            aria-expanded={timeslotsOpen}
                        >
                            <span className="block text-sm font-medium text-gray-700">
                                Tranches horaires ({validation.timeslots.length})
                            </span>
                            <svg
                                className={`w-5 h-5 text-gray-500 transition-transform ${timeslotsOpen ? "rotate-180" : ""}`}
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {timeslotsOpen && (
                            <div className="mt-2 max-h-64 overflow-y-auto space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                {validation.timeslots.map((ts) => (
                                    <div
                                        key={ts.id}
                                        className="flex items-center justify-between p-2 bg-white rounded border border-gray-200"
                                    >
                                        <div className="flex items-center space-x-2">
                                            <svg
                                                className="w-4 h-4 text-gray-400"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                />
                                            </svg>
                                            <span className="text-sm text-gray-700">
                                                {ts.hour_type.name}
                                            </span>
                                        </div>
                                        <span className="text-sm font-medium text-gray-900">
                                            {formatDateHours(ts.start_date)} - {formatDateHours(ts.end_date)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Commentaire
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 min-h-[100px]">
                        <p className="text-gray-900 whitespace-pre-wrap">
                            {validation.remarks || 'Aucun commentaire'}
                        </p>
                    </div>
                </div>

                {validation.response && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Réponse
                        </label>
                        <div className={`p-3 rounded-lg border ${statusConfig.bg} ${statusConfig.border}`}>
                            <p className={`${statusConfig.text} whitespace-pre-wrap`}>
                                {validation.response}
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Actions */}
            {isPending && !isValidation && (
                <div className="flex justify-center space-x-3 pt-4 border-t border-gray-200">
                    <Button
                        id="cancel"
                        type="red"
                        action={_cancel}
                        name="Annulation de la demande"
                        loading={isProcessing}
                    />
                </div>
            )}
            {isPending && isValidation && (
                <>
                    <div className="w-full px-2 mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Réponse
                        </label>
                        <InputForm
                            name=""
                            type="textarea"
                            required={false}
                            value={response}
                            set={setResponse}
                        />
                    </div>
                    <div className="flex justify-center space-x-3 pt-4 border-t border-gray-200">
                        <Button
                            id="refuse"
                            type="red"
                            action={_refuse}
                            name="Refuser la demande"
                            loading={isProcessing}
                        />
                        <Button
                            id="validate"
                            action={_validate}
                            name="Valider la demande"
                            loading={isProcessing}
                        />
                    </div>
                </>
            )
            }
        </div >
    );
};

const ValidationsList = (props) => {
    const { defaultTab } = props;
    const { pathname } = useLocation();
    const [user, setUser, currentApp, currentMenu, currentTab, setCurrentTab, setCurrentMenu] = useOutletContext();
    const [data, setData] = useState([]);
    const [selectedTab, setSelectedTab] = useState(defaultTab || "demandes");
    const [openSideBar, setOpenSidebar] = useState(false);
    const [sideBarTitle, setSidebarTitle] = useState("");
    const [sideBarContent, setSidebarContent] = useState("");
    const [isLoading, setIsLoading] = useState(true);

    const status = useLocation().hash.split("#")[1] || "PENDING";

    const loadValidation = () => {
        setIsLoading(true);
        getAskValidators(status)
            .then((resp) => {
                setData(resp.data || []);
            })
            .catch((error) => {
                toast.error("Erreur lors du chargement des validations.");
                setData([]);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const loadMyAsk = () => {
        setIsLoading(true);
        getMyAsk(status)
            .then((resp) => {
                setData(resp.data || []);
            })
            .catch((error) => {
                toast.error("Erreur lors du chargement de vos demandes.");
                setData([]);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        if (selectedTab === "validations") {
            loadValidation();
        } else {
            loadMyAsk();
        }
    }, [pathname, selectedTab, status]);

    const rowClick = (validation) => {
        const isPlanningCorrection = validation.hour_type == null && validation.hour_type_id == null;
        setSidebarTitle(
            isPlanningCorrection
                ? "Demande de correction de planning"
                : ("Demande de " + (validation.hour_type?.name || "validation"))
        );
        setSidebarContent(
            <AskValidationForm
                closeModal={() => setOpenSidebar(false)}
                validation={validation}
                isValidation={selectedTab === "validations"}
                refreshData={
                    selectedTab === "validations" ? loadValidation : loadMyAsk
                }
            />
        );
        setOpenSidebar(true);
    };

    return (
        <div className="min-h-full bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">
                                Demandes et validations
                            </h1>
                            <p className="mt-2 text-sm text-gray-600">
                                Gérez vos demandes de validation et validez celles qui vous sont adressées
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Tabs de sélection */}
                <div className="mb-6">
                    <div className="flex space-x-4">
                        <button
                            onClick={() => setSelectedTab("demandes")}
                            className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${selectedTab === "demandes"
                                ? "bg-app-primary text-white shadow-md"
                                : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                                }`}
                        >
                            Mes demandes
                        </button>
                        <button
                            onClick={() => setSelectedTab("validations")}
                            className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${selectedTab === "validations"
                                ? "bg-app-primary text-white shadow-md"
                                : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                                }`}
                        >
                            Mes validations
                        </button>
                    </div>
                </div>

                {/* Filtres par statut */}
                <div className="mb-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex flex-wrap gap-2">
                            {requestMenu.map((element) => {
                                const isActive = status === element.to.split("#")[1];
                                return (
                                    <Link
                                        key={element.to}
                                        to={element.to}
                                        className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive
                                            ? "bg-app-primary text-white shadow-md"
                                            : "bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200"
                                            }`}
                                    >
                                        <svg
                                            className="w-4 h-4 mr-2"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d={element.svg}
                                            />
                                        </svg>
                                        {element.title}
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Liste des demandes */}
                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-app-primary"></div>
                    </div>
                ) : data.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                        <svg
                            className="mx-auto h-12 w-12 text-gray-400"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                        </svg>
                        <h3 className="mt-4 text-lg font-medium text-gray-900">
                            Aucune demande trouvée
                        </h3>
                        <p className="mt-2 text-sm text-gray-500">
                            {selectedTab === "demandes"
                                ? "Vous n'avez aucune demande pour ce statut."
                                : "Aucune demande ne vous est adressée pour ce statut."}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {data.map((validation) => (
                            <ValidationCard
                                key={validation.id}
                                validation={validation}
                                onClick={rowClick}
                            />
                        ))}
                    </div>
                )}
            </div>

            <SideBar
                title={sideBarTitle}
                content={sideBarContent}
                isOpen={openSideBar}
                onClose={() => {
                    setOpenSidebar(false);
                }}
            />
        </div>
    );
};

export default ValidationsList;
